import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MDAvatar from "components/MDAvatar";
import Axios from "axios";
import Cookies from "js-cookie";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { useCategories, useUpdateCategory, useDeleteCategory } from "api/useCategories.ts";

export default function categoriesTableData() {
  const { t } = useTranslation();
  const { data: fetchedCategories, isLoading } = useCategories();
  const deleteCategory = useDeleteCategory();
  const updateCategory = useUpdateCategory();
  const [categories, setCategories] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isIcon, setIsIcon] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isVideo, setIsVideo] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const [editedCategory, setEditedCategory] = useState({
    _id: "",
    name: {
      en: "",
      ar: "",
    },
    description: {
      en: "",
      ar: "",
    },
    minSupplyQuantity: 0,
    allowedMosques: "",
    state: "",
  });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleOpenFileModal = (categoryId) => {
    setSelectedId(categoryId);
    setSelectedFile(null);
    setImagePreview(null);
    setIsFileModalOpen(true);
  };

  const closeFileModal = () => {
    setIsFileModalOpen(false);
    setSelectedFile(null);
    setImagePreview(null);
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      setNotification({
        open: true,
        message: t("common.noFileSelected"),
        severity: "error",
      });
      return;
    }

    setIsUploading(true);

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Unauthorized access");
      }

      const endpoint = isVideo ? "video" : isIcon ? "icon" : "image";

      const response = await Axios.patch(
        `${process.env.REACT_APP_API_URL}/categories/update-${endpoint}/${selectedId}`,
        { [isVideo ? "video" : "photo"]: selectedFile },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotification({
        open: true,
        message: t("categories.mediaUploaded", {
          type: isVideo
            ? t("categories.media.video")
            : isIcon
            ? t("categories.media.icon")
            : t("categories.media.image"),
        }),
        severity: "success",
      });

      setImagePreview(null);
      setSelectedFile(null);
      closeFileModal();
    } catch (error) {
      console.error("Error uploading file:", error.response || error.message);
      setNotification({
        open: true,
        message: t("categories.mediaUploadFailed", {
          type: isVideo
            ? t("categories.media.video")
            : isIcon
            ? t("categories.media.icon")
            : t("categories.media.image"),
        }),
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openDeleteModal = (categoryId) => {
    setSelectedId(categoryId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleEditClick = (category) => {
    setEditedCategory({
      _id: category._id,
      name: category.name,
      description: category.description,
      minSupplyQuantity: category.minSupplyQuantity,
      allowedMosques: category.allowedMosques,
      state: category.state,
    });
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditedCategory({
      _id: "",
      name: {
        en: "",
        ar: "",
      },
      description: {
        en: "",
        ar: "",
      },
      minSupplyQuantity: 0,
      allowedMosques: "",
      state: "",
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name?.includes(".")) {
      const keys = name.split(".");

      setEditedCategory((prev) => {
        if (keys.length === 2) {
          return {
            ...prev,
            [keys[0]]: {
              ...(prev?.[keys[0]] || {}), // Ensure it exists
              [keys[1]]: value,
            },
          };
        }
        return { ...prev, [name]: value };
      });
    } else {
      setEditedCategory((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedId) return;

    deleteCategory.mutate(selectedId, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: t("categories.categoryDeleted"),
          severity: "success",
        });
        closeDeleteModal();
      },
      onError: (error) => {
        console.error("Error deleting Category:", error);
        setNotification({
          open: true,
          message: t("categories.categoryDeleteFailed"),
          severity: "error",
        });
      },
    });
  };
  const handleSaveChanges = async () => {
    const originalCategory = categories.find((c) => c._id === editedCategory._id);

    const updatedFields = Object.keys(editedCategory).reduce((changes, key) => {
      if (editedCategory[key] !== originalCategory[key]) {
        changes[key] = editedCategory[key];
      }
      return changes;
    }, {});

    if (Object.keys(updatedFields).length === 0) {
      setNotification({
        open: true,
        message: t("common.noChanges"),
        severity: "info",
      });
      return;
    }
    const updatedCategory = {
      _id: editedCategory._id,
      name: editedCategory.name,
      description: editedCategory.description,
      minSupplyQuantity: editedCategory.minSupplyQuantity,
      allowedMosques: editedCategory.allowedMosques,
      state: editedCategory.state,
    };

    updateCategory.mutate(updatedCategory, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: t("categories.categoryUpdated"),
          severity: "success",
        });
        handleCloseModal();
      },
      onError: (error) => {
        console.error("Error updating Category:", error);
        setNotification({
          open: true,
          message: t("categories.categoryUpdateFailed"),
          severity: "error",
        });
      },
    });
  };

  useEffect(() => {
    !isLoading && setCategories(fetchedCategories);
  }, [fetchedCategories, isLoading]);

  const rows = useMemo(() => {
    return isLoading
      ? Array(10)
          .fill(0)
          .map((_, index) => ({
            categoryId: index + 1,
            categoryName: t("common.loading"),
            categoryImg: <MDAvatar size="xl" />,
            categoryIcon: <MDAvatar size="xl" />,
            description: t("common.loading"),
            action: <div className="flex justify-center space-x-2">{t("common.loading")}</div>,
          }))
      : Array.isArray(categories) &&
          categories.map((category) => ({
            categoryId: category._id,
            categoryName: (
              <div className="max-w-[100px] truncate">
                <p className="font-semibold" title={category.name.en || category.name.ar}>
                  {category.name.en || category.name.ar}
                </p>
              </div>
            ),
            categoryImg: (
              <MDAvatar
                src={category.image || "https://via.placeholder.com/150"}
                name={category.name.en || category.name.ar}
                size="xl"
              />
            ),
            categoryIcon: (
              <MDAvatar
                src={category.icon || "https://via.placeholder.com/150"}
                name={category.name.en || category.name.ar}
                size="xl"
              />
            ),
            description: category.description.en || category.description.ar,
            minSupplyQuantity: category.minSupplyQuantity || 0,
            releaseDate: new Date(category.createdAt).toLocaleDateString(),

            action: (
              <div className="flex justify-center space-x-2">
                <IconButton color="info" size="medium" onClick={() => handleEditClick(category)}>
                  <EditIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="success"
                  size="medium"
                  onClick={() => {
                    setIsIcon(false);
                    handleOpenFileModal(category._id);
                  }}
                >
                  <AddPhotoAlternateIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="warning"
                  size="medium"
                  onClick={() => {
                    setIsIcon(true);
                    handleOpenFileModal(category._id);
                  }}
                >
                  <AddReactionIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="info"
                  size="medium"
                  onClick={() => {
                    setIsVideo(true);
                    handleOpenFileModal(category._id);
                  }}
                >
                  <VideoCallIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="error"
                  size="medium"
                  onClick={() => openDeleteModal(category._id)}
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </div>
            ),
          }));
  }, [isLoading, categories]);

  return {
    columns: [
      { Header: t("categories.columns.id"), accessor: "categoryId", align: "center" },
      { Header: t("categories.columns.categoryName"), accessor: "categoryName", align: "center" },
      { Header: t("categories.columns.categoryImage"), accessor: "categoryImg", align: "center" },
      { Header: t("categories.columns.categoryIcon"), accessor: "categoryIcon", align: "center" },
      { Header: t("categories.columns.description"), accessor: "description", align: "center" },
      {
        Header: t("categories.columns.minSupplyQty"),
        accessor: "minSupplyQuantity",
        align: "center",
      },
      { Header: t("categories.columns.releaseDate"), accessor: "releaseDate", align: "center" },
      { Header: t("categories.columns.actions"), accessor: "action", align: "center" },
    ],
    rows,
    editedCategory,
    handleCloseModal,
    isModalOpen,
    notification,
    setNotification,
    handleSaveChanges,
    handleCloseNotification,
    handleInputChange,
    handleDeleteCategory,
    isDeleteModalOpen,
    closeDeleteModal,
    handleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isIcon,
    isUploading,
    isVideo,
  };
}
